import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import ChatBubble from './ChatBubble';
import ChatHeader from './ChatHeader';
import InputChat from './InputChat';
import SideBarChat from './SidebarChat';
import ProvidersList from './ProvidersList';
import { Icon } from '../../components/shared';
import {
  generateChatId,
  listenToChannels,
  listenToAllChannels,
  listenToChatMessages,
  listenToChats,
  listenToNotifications,
  removeNotification,
} from 'firebase/firebaseFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { getAllProviderResources, getAllProvidersWithPlans } from '@guryou/redux';
import { Spinner } from '@guryou/html-components';
import SidebarChannel from './SidebarChannel';
import { useWidgetAuthLockedProvider } from '../../hooks/useWidgetAuthLockedProvider';
import './chat.css';

function Chat() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { userId } = useSelector(state => state.auth.data);
  const auth = useSelector(state => state.auth);
  const scrollRef = useRef(null);
  const [loadingChats, setLoadingChats] = useState(true);
  const [chats, setChats] = useState([]);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatActive, setChatActive] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [currentTab, setCurrentTab] = useState('chat');
  const [channels, setChannels] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [providerUnreadCounts, setProviderUnreadCounts] = useState({});
  const [providerLastMessageTime, setProviderLastMessageTime] = useState({});
  const [hasUnreadChats, setHasUnreadChats] = useState(false);
  const [hasUnreadChannels, setHasUnreadChannels] = useState(false);
  const chatCountsRef = useRef({});
  const channelCountsRef = useRef({});
  const lockProviderId = useWidgetAuthLockedProvider();

  const providersWithPlans = useSelector(state => state.providersWithPlans);
  const providersResources = useSelector(state => state.providersResources);

  useEffect(() => {
    if (!userId) return;

    const updateProviderCounts = (timestamps = {}) => {
      const merged = {};

      Object.keys(chatCountsRef.current).forEach(providerId => {
        merged[providerId] = (merged[providerId] || 0) + chatCountsRef.current[providerId];
      });

      Object.keys(channelCountsRef.current).forEach(providerId => {
        merged[providerId] = (merged[providerId] || 0) + channelCountsRef.current[providerId];
      });

      setProviderUnreadCounts(merged);

      if (Object.keys(timestamps).length > 0) {
        setProviderLastMessageTime(prev => ({ ...prev, ...timestamps }));
      }
    };
    const unsubscribeChats = listenToNotifications(userId, notifications => {
      const unreadChats = notifications?.unreadChats || [];
      const newChatCounts = {};
      const timestamps = {};

      unreadChats.forEach(notification => {
        const chatId = notification.chatId;
        const parts = chatId.split('_');

        if (parts.length >= 3) {
          const providerId = parseInt(parts[parts.length - 1]);
          newChatCounts[providerId] = (newChatCounts[providerId] || 0) + 1;

          const timestamp = notification.timestamp || 0;
          if (!timestamps[providerId] || timestamp > timestamps[providerId]) {
            timestamps[providerId] = timestamp;
          }
        }
      });

      chatCountsRef.current = newChatCounts;
      updateProviderCounts(timestamps);
    });

    const unsubscribeChannels = listenToAllChannels(userId, unreadChannels => {
      const newChannelCounts = {};
      const timestamps = {};

      unreadChannels.forEach(channel => {
        const providerId = channel.providerId;
        if (providerId) {
          newChannelCounts[providerId] = (newChannelCounts[providerId] || 0) + 1;

          const timestamp = new Date(channel.lastMessage?.createdAt || 0).getTime();
          if (!timestamps[providerId] || timestamp > timestamps[providerId]) {
            timestamps[providerId] = timestamp;
          }
        }
      });

      channelCountsRef.current = newChannelCounts;
      updateProviderCounts(timestamps);
    });

    return () => {
      if (typeof unsubscribeChats === 'function') {
        unsubscribeChats();
      }
      if (typeof unsubscribeChannels === 'function') {
        unsubscribeChannels();
      }
    };
  }, [userId]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  function changeActiveChat(chatActive) {
    setChatActive(chatActive);
    if (chatActive?.id) {
      window.activeChatId = chatActive.id;
    } else {
      window.activeChatId = null;
    }
    if (isMobileView) {
      setShowChat(true);
      document.body.classList.add('chat-modal-open');
    }
  }

  const changeProvider = useCallback(
    provider => {
      setCurrentProvider(provider);
      setChatActive(null);
      window.activeChatId = null;
      if (isMobileView) {
        setShowChat(false);
      }
    },
    [isMobileView]
  );

  const changeTab = tab => {
    setCurrentTab(tab);
    setChatActive(null);
    window.activeChatId = null;
    if (isMobileView) {
      setShowChat(false);
    }
  };

  const handleBackToSidebar = () => {
    setShowChat(false);
    document.body.classList.remove('chat-modal-open');
  };

  useEffect(() => {
    if (auth?.data?.userId) {
      dispatch(getAllProvidersWithPlans(+auth.data.userId));
    }
  }, [auth.data.userId, dispatch]);

  const providersData = providersWithPlans?.data;

  useEffect(() => {
    if (providersData?.length > 0) {
      setLoadingChats(false);
    }
  }, [providersData, providersWithPlans]);

  useEffect(() => {
    if (!lockProviderId || !providersData?.length) return;
    if (currentProvider?.id && String(currentProvider.id) === String(lockProviderId)) return;

    const lockedProvider = providersData.find(provider => String(provider?.id) === String(lockProviderId));
    if (lockedProvider) {
      changeProvider(lockedProvider);
    }
  }, [lockProviderId, providersData, currentProvider, changeProvider]);

  useEffect(() => {
    if (currentProvider?.id) {
      dispatch(getAllProviderResources(currentProvider.id));
    }
  }, [currentProvider, dispatch]);

  useEffect(() => {
    if (!chatActive) {
      return;
    }

    setLoadingMessage(true);

    const maybeUnsub = listenToChatMessages(currentTab, chatActive.id, newMessages => {
      setMessages(newMessages);
      setLoadingMessage(false);
      const run = async () => {
        await removeNotification(userId, chatActive.id);
      };
      run();
    });

    return () => {
      if (typeof maybeUnsub === 'function') {
        maybeUnsub();
      }
    };
  }, [chatActive, currentTab, userId]);

  useEffect(() => {
    if (!currentProvider) return;

    const unsubscribe = listenToChats(userId, +currentProvider.id, updatedChats => {
      const chats = updatedChats
        .map(chat => {
          // Find the member that is not the current user
          const otherMember = providersResources?.data?.find(member => chat?.members?.includes(+member.id) && +member.id !== +userId);
          return otherMember ? { ...otherMember, chatId: chat.id, members: chat.members } : null;
        })
        .filter(chat => chat !== null); // Remove null/undefined values

      setChats(chats);
      setLoadingChats(false);
    });

    return () => unsubscribe();
  }, [chatActive, currentProvider, providersResources.data, userId]);

  useEffect(() => {
    const hasUnread = chats?.some(chat => chat?.unreadChats === true);
    setHasUnreadChats(hasUnread);
  }, [chats]);

  useEffect(() => {
    const hasUnread = channels?.some(channel => channel.unreadChats === true);
    setHasUnreadChannels(hasUnread);
  }, [channels]);

  useEffect(() => {
    const maybeUnsub = listenToNotifications(userId, newNotifications => {
      const unreadChatMap = new Map((newNotifications?.unreadChats || []).map(n => [n.chatId, n.timestamp || 0]));

      const chatsFiltered = chats?.map(chat => {
        const chatId = generateChatId([userId, chat?.id], +currentProvider?.id);
        const isUnread = unreadChatMap.has(chatId);
        const timestamp = unreadChatMap.get(chatId) || 0;

        return {
          ...chat,
          unreadChats: chatActive?.id === chatId ? false : isUnread,
          lastNotificationTimestamp: timestamp,
        };
      });

      const sortedChats = [...(chatsFiltered || [])].sort((a, b) => {
        if (a.unreadChats !== b.unreadChats) {
          return a.unreadChats ? -1 : 1;
        }

        return b.lastNotificationTimestamp - a.lastNotificationTimestamp;
      });

      setChats(sortedChats);
    });

    return () => {
      if (typeof maybeUnsub === 'function') {
        maybeUnsub();
      }
    };
  }, [chatActive, chats, currentProvider, userId]);

  useEffect(() => {
    if (!userId || !+currentProvider?.id) {
      return;
    }

    const unsubscribe = listenToChannels(+currentProvider?.id, updatedChannels => {
      const channels = updatedChannels
        .map(channel => {
          const unreadChats = channel?.lastMessage?.notificationIds?.includes(+userId);
          return {
            ...channel,
            unreadChats,
          };
        })
        .sort((a, b) => {
          if (a.unreadChats !== b.unreadChats) {
            return a.unreadChats ? -1 : 1;
          }

          const dateA = new Date(a.lastMessage?.createdAt || 0).getTime();
          const dateB = new Date(b.lastMessage?.createdAt || 0).getTime();
          return dateB - dateA;
        });

      setChannels(channels);
      setLoadingChats(false);
    });

    return () => unsubscribe();
  }, [currentProvider, userId]);

  useEffect(() => {
    const container = scrollRef.current;
    if (container && !loadingMessage) {
      container.scrollTop = container.scrollHeight;
    }
  }, [loadingMessage, messages]);

  useEffect(() => {
    return () => {
      document.body.classList.remove('chat-modal-open');
    };
  }, []);

  return (
    <div class="flex h-screen relative overflow-hidden bg-white">
      <div class={`${isMobileView ? (showChat ? 'hidden' : 'block') : 'block'} ${isMobileView ? 'w-full' : 'w-1/4 min-w-[386.5px]'} bg-white`}>
        {!currentProvider ? (
          <ProvidersList
            providers={providersWithPlans.data || []}
            changeProvider={changeProvider}
            currentProvider={currentProvider}
            loadingChats={loadingChats}
            providerUnreadCounts={providerUnreadCounts}
            providerLastMessageTime={providerLastMessageTime}
          />
        ) : (
          <>
            {currentTab === 'chat' && (
              <SideBarChat
                changeProvider={changeProvider}
                currentProvider={currentProvider}
                providers={providersWithPlans.data || []}
                chats={chats || []}
                chatActive={chatActive}
                setActiveChat={changeActiveChat}
                setLoadingMessage={setLoadingMessage}
                loadingChats={loadingChats}
                users={providersResources?.data}
                currentTab={currentTab}
                setCurrentTab={changeTab}
                hasUnreadChats={hasUnreadChats}
                hasUnreadChannels={hasUnreadChannels}
              />
            )}
            {currentTab === 'channel' && (
              <SidebarChannel
                chatActive={chatActive}
                setActiveChat={changeActiveChat}
                setLoadingMessage={setLoadingMessage}
                currentTab={currentTab}
                setCurrentTab={changeTab}
                channels={channels}
                providers={providersWithPlans.data || []}
                changeProvider={changeProvider}
                currentProvider={currentProvider}
                hasUnreadChats={hasUnreadChats}
                hasUnreadChannels={hasUnreadChannels}
              />
            )}
          </>
        )}
      </div>

      {!isMobileView && (
        <div class="flex-1 flex flex-col bg-white">
          {loadingMessage && (
            <div class="flex items-center justify-center h-full w-full bg-white">
              <div role="status" class="flex flex-col items-center">
                <Spinner />
                <p class="text-sm text-gray-500 mt-4">{t('CHAT.loadingMessages')}</p>
              </div>
            </div>
          )}
          {!loadingMessage && !chatActive && (
            <div class="flex items-center justify-center h-full w-full bg-white">
              <div class="text-center px-4">
                <div class="w-24 h-24 mx-auto mb-6 rounded-full bg-[var(--feel-primary)]/10 flex items-center justify-center">
                  <Icon name="chat-alt" className="text-[var(--feel-primary)]" size="2xl" />
                </div>
                <h3 class="text-xl font-semibold text-gray-900 mb-2">{t('CHAT.selectConversation')}</h3>
                <p class="text-sm text-gray-500">{t('CHAT.startChattingHere')}</p>
              </div>
            </div>
          )}
          {!loadingMessage && chatActive && (
            <>
              <ChatHeader currentChat={chatActive} currentTab={currentTab} onBack={handleBackToSidebar} isMobile={isMobileView} />
              <div ref={scrollRef} class="flex-1 p-6 overflow-y-auto bg-white">
                {messages.length === 0 && (
                  <div class="flex items-center justify-center h-full">
                    <div class="text-center">
                      <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                        <Icon name="chat" className="text-gray-400" size="xl" />
                      </div>
                      <p class="text-sm font-medium text-gray-500">{t('CHAT.noMessagesYet')}</p>
                      <p class="text-xs text-gray-400 mt-1">{t('CHAT.startChatting')}</p>
                    </div>
                  </div>
                )}
                {messages.map(message => (
                  <ChatBubble currentChat={chatActive} isMine={message.senderId === +userId} message={message} key={message.id} />
                ))}
              </div>
              {chatActive && currentTab === 'chat' && (
                <InputChat currentProvider={currentProvider} currentChat={chatActive} messages={messages} setMessages={setMessages} />
              )}
            </>
          )}
        </div>
      )}

      {isMobileView &&
        showChat &&
        chatActive &&
        createPortal(
          <div class="chat-fullscreen-modal">
            <div class="flex flex-col h-full">
              {loadingMessage && (
                <div class="flex items-center justify-center h-full w-full">
                  <div role="status">
                    <Spinner />
                  </div>
                </div>
              )}
              {!loadingMessage && (
                <>
                  {chatActive && <ChatHeader currentChat={chatActive} currentTab={currentTab} onBack={handleBackToSidebar} isMobile={isMobileView} />}
                  {chatActive && (
                    <div ref={scrollRef} class="flex-1 p-4 overflow-y-auto space-y-4">
                      {messages.length === 0 && <p class="text-sm text-gray-500 text-center">Start by writing a message!</p>}
                      {messages.map(message => (
                        <ChatBubble currentChat={chatActive} isMine={message.senderId === +userId} message={message} key={message.id} />
                      ))}
                    </div>
                  )}
                  {chatActive && currentTab === 'chat' && <InputChat currentChat={chatActive} messages={messages} setMessages={setMessages} />}
                </>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default Chat;
